import { useTheme } from "@shopify/restyle";
import React, { RefObject } from "react";
import { Platform, TextInput, TextStyle } from "react-native";

import {
  BaseInputProps,
  HStack,
  Input,
  InputType,
  OnChangeType,
} from "../../primitive";

export type SubtleInputProps<T extends InputType> = BaseInputProps<T> & {
  leftIcon?: React.JSX.Element;
  rightIcon?: React.JSX.Element;
  hasError?: boolean;
  isDisabled?: boolean;
  name: string;
  placeholder?: string;
  testID?: string;
  value: string | number;
  type?: InputType;
  onChange: OnChangeType<T>;
  inputRef?: RefObject<TextInput | null>;
};

export const SubtleInput = <T extends InputType = "text">({
  testID,
  name,
  placeholder,
  leftIcon,
  rightIcon,
  hasError,
  isDisabled,
  value,
  type,
  onChange,
  inputRef,
  ...rest
}: SubtleInputProps<T>) => {
  const theme = useTheme();

  const placeholderColor = theme.colors["text-secondary"];
  const styleProps = Platform.OS === "web" ? { outlineWidth: 0 } : {};

  const textColor = isDisabled
    ? "text-disabled"
    : hasError
    ? "text-error"
    : "text-primary";

  return (
    <HStack gap={2} alignItems="center">
      {leftIcon
        ? React.cloneElement(leftIcon, {
            color: textColor,
          })
        : null}
      <Input
        {...rest}
        inputRef={inputRef}
        onChange={onChange}
        name={name}
        value={value}
        placeholder={placeholder}
        editable={!isDisabled}
        focusable={!isDisabled}
        testID={testID}
        color={textColor}
        placeholderTextColor={placeholderColor}
        // A work around since restyle doens't support outlineWidth on native
        style={{ ...styleProps } as TextStyle}
        {...theme.textVariants["mainHeading"]}
        flexGrow={1}
        secureTextEntry={type === "password"}
      />
      {rightIcon
        ? React.cloneElement(rightIcon, {
            color: textColor,
          })
        : null}
    </HStack>
  );
};
